import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VisiteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fichierRef = useRef(null);
  const [visite, setVisite] = useState(null);
  const [apercu, setApercu] = useState(null); // dénonce en aperçu
  const [envoiEnCours, setEnvoiEnCours] = useState(null);
  const [depotEnCours, setDepotEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/visites/${id}`);
      setVisite(data);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? 'Visite introuvable' : 'Erreur de chargement');
    }
  }, [id]);

  useEffect(() => { charger(); }, [charger]);

  const ouvrirBon = async () => {
    try {
      const { data } = await axios.get(`${API}/visites/${id}/bon.pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(data), '_blank', 'noopener');
    } catch {
      toast.error("Impossible d'ouvrir le bon de visite");
    }
  };

  const ouvrirDocument = async (documentId) => {
    try {
      const { data } = await axios.get(`${API}/documents/${documentId}/download`, { responseType: 'blob' });
      window.open(URL.createObjectURL(data), '_blank', 'noopener');
    } catch {
      toast.error("Impossible d'ouvrir ce document");
    }
  };

  /** Retour à la page d'origine (fiche contact ou fiche bien), à défaut la fiche acquéreur. */
  const terminer = () => {
    const retour = params.get('retour');
    navigate(retour || `/contacts/${visite?.contact_id || ''}`);
  };

  const deposerScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDepotEnCours(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('entity_type', 'contact');
      form.append('entity_id', visite.contact_id);
      form.append('type_document', 'bon_visite_signe');
      const { data: doc } = await axios.post(`${API}/documents/upload`, form);
      await axios.post(`${API}/visites/${id}/scan`, { document_id: doc.id });
      toast.success(visite.document_bon_id ? 'Scan remplacé' : 'Bon signé rattaché');
      charger();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Échec du dépôt');
    } finally {
      setDepotEnCours(false);
      if (fichierRef.current) fichierRef.current.value = '';
    }
  };

  const envoyerDenonce = async (proprietaireId) => {
    // Bon non signé : on demande confirmation avant d'informer le propriétaire.
    if (visite?.statut_bon !== 'signe'
        && !window.confirm("Le bon de visite n'est pas encore signé. Envoyer la dénonce quand même ?")) {
      return;
    }
    setEnvoiEnCours(proprietaireId);
    try {
      await axios.post(`${API}/visites/${id}/denonce/${proprietaireId}/envoyer`);
      toast.success('Dénonce envoyée');
      setApercu(null);
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setEnvoiEnCours(null);
    }
  };

  if (!visite) {
    return (
      <div className="min-h-[60vh] bg-slate-50 flex items-center justify-center" data-testid="visite-loading">
        <i className="bi bi-arrow-repeat animate-spin text-2xl text-slate-400" />
      </div>
    );
  }

  const signe = visite.statut_bon === 'signe';
  const papier = visite.mode_signature !== 'telephone';
  const bonRattache = Boolean(visite.document_bon_id);
  const scansPrecedents = visite.scans_precedents || [];

  return (
    <div className="min-h-full bg-slate-50 pb-10" data-testid="visite-detail-page">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="visite-detail-back-btn"
          className="w-11 h-11 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center"
        >
          <i className="bi bi-arrow-left text-lg" />
        </button>
        <h1 className="text-base md:text-lg font-bold text-slate-900">Visite</h1>
        <span
          className={`ml-auto px-2 py-1 rounded text-[10px] font-bold uppercase ${
            signe ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
          }`}
          data-testid="visite-statut-bon"
        >
          {signe ? 'Bon signé' : 'Bon à joindre'}
        </span>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Récapitulatif */}
        <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="visite-recap">
          <h2 className="text-sm font-bold text-slate-900 mb-3">Récapitulatif</h2>
          <dl className="text-sm divide-y divide-slate-100">
            <div className="py-2 flex justify-between gap-3">
              <dt className="text-slate-500">Acquéreur</dt>
              <dd className="font-semibold text-slate-900 text-right">{visite.acquereur?.nom_complet || '—'}</dd>
            </div>
            <div className="py-2 flex justify-between gap-3">
              <dt className="text-slate-500">Date de la visite</dt>
              <dd className="font-semibold text-slate-900 text-right">
                {visite.date_visite ? new Date(visite.date_visite).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
              </dd>
            </div>
            <div className="py-2 flex justify-between gap-3">
              <dt className="text-slate-500">Négociateur</dt>
              <dd className="font-semibold text-slate-900 text-right">{visite.negociateur_nom || '—'}</dd>
            </div>
          </dl>
          <ul className="mt-3 space-y-2">
            {(visite.biens_resume || []).map((b) => (
              <li key={b.id} className="bg-slate-50 rounded-lg px-3 py-2.5" data-testid={`visite-bien-${b.id}`}>
                <p className="text-sm font-semibold text-slate-900">{b.reference}</p>
                <p className="text-xs text-slate-500">{[b.nature, b.activite, b.commune, b.prix].filter(Boolean).join(' · ')}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* Bon de visite */}
        <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="visite-bon">
          <h2 className="text-sm font-bold text-slate-900 mb-3">Bon de visite</h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={ouvrirBon}
              data-testid="visite-bon-pdf-btn"
              className="h-11 px-4 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold flex items-center justify-center gap-2"
            >
              <i className="bi bi-file-earmark-pdf" />
              {bonRattache ? 'Voir le bon signé' : 'Voir le PDF'}
            </button>
            {papier && (
              <>
                <button
                  type="button"
                  onClick={() => fichierRef.current?.click()}
                  disabled={depotEnCours}
                  data-testid={bonRattache ? 'visite-scan-replace-btn' : 'visite-scan-btn'}
                  className="h-11 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <i className={`bi ${bonRattache ? 'bi-arrow-repeat' : 'bi-upload'}`} />
                  {depotEnCours ? 'Envoi…' : bonRattache ? 'Remplacer le scan' : 'Bon signé à joindre'}
                </button>
                <input
                  ref={fichierRef}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={deposerScan}
                  data-testid="visite-scan-input"
                  className="hidden"
                />
              </>
            )}
          </div>
          {visite.empreinte_bon && (
            <p className="mt-3 text-xs text-slate-500 break-all" data-testid="visite-empreinte">
              Empreinte : {visite.empreinte_bon}
            </p>
          )}
          {scansPrecedents.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100" data-testid="visite-scans-precedents">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                Scans précédents
              </h3>
              <ul className="space-y-1.5">
                {scansPrecedents.map((s) => (
                  <li key={s.document_id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-600 min-w-0 truncate">
                      {s.depose_le ? new Date(s.depose_le).toLocaleDateString('fr-FR') : '—'}
                      {s.depose_par ? ` · ${s.depose_par}` : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => ouvrirDocument(s.document_id)}
                      data-testid={`visite-scan-precedent-${s.document_id}`}
                      className="text-blue-700 font-semibold underline shrink-0"
                    >
                      Ouvrir
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Dénonces */}
        <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="visite-denonces">
          <h2 className="text-sm font-bold text-slate-900 mb-3">Dénonces aux propriétaires</h2>
          {(visite.denonces || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-2">Aucun propriétaire renseigné sur ces biens</p>
          ) : (
            <ul className="space-y-3">
              {visite.denonces.map((d) => (
                <li key={d.proprietaire_id} className="border border-slate-200 rounded-lg p-3" data-testid={`denonce-${d.proprietaire_id}`}>
                  <p className="text-sm font-semibold text-slate-900">{d.proprietaire_nom}</p>
                  {d.email_destinataire ? (
                    <p className="text-xs text-slate-500">{d.email_destinataire}</p>
                  ) : (
                    <p className="text-xs text-rose-600">
                      Ajoutez l’e-mail du propriétaire —{' '}
                      <a href={`/contacts/${d.proprietaire_id}`} className="underline font-semibold">
                        ouvrir sa fiche
                      </a>
                    </p>
                  )}
                  {d.envoyee_le ? (
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-emerald-700" data-testid={`denonce-envoyee-${d.proprietaire_id}`}>
                        Envoyée le {new Date(d.envoyee_le).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                      </p>
                      <button
                        type="button"
                        onClick={() => setApercu({ ...d, corps: d.corps_envoye || d.corps, sujet: d.sujet_envoye || d.sujet, lecture: true })}
                        data-testid={`denonce-voir-envoye-${d.proprietaire_id}`}
                        className="mt-1 text-xs text-blue-700 font-semibold underline"
                      >
                        Voir le texte envoyé
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setApercu({ ...d, lecture: false })}
                        data-testid={`denonce-apercu-${d.proprietaire_id}`}
                        className="h-11 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold"
                      >
                        Voir
                      </button>
                      <button
                        type="button"
                        onClick={() => envoyerDenonce(d.proprietaire_id)}
                        disabled={!d.email_destinataire || envoiEnCours === d.proprietaire_id}
                        data-testid={`denonce-envoyer-${d.proprietaire_id}`}
                        className="h-11 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold"
                      >
                        {envoiEnCours === d.proprietaire_id ? 'Envoi…' : 'Envoyer'}
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Téléphone : espace au-dessus de la barre « Terminer » et du menu du bas. */}
        <div
          className="md:hidden"
          style={{ height: 'calc(138px + env(safe-area-inset-bottom, 0px))' }}
          aria-hidden="true"
        />
      </main>

      {/* Téléphone : barre fixée juste au-dessus du menu du bas, comme « Enregistrer la
          visite ». Ordinateur : bouton dans le flux de la page. */}
      <footer
        className="fixed md:static inset-x-0 bg-white border-t border-slate-200 md:border-0 md:bg-transparent px-4 py-3 md:px-0 md:pt-0 z-30"
        style={{ bottom: 'calc(58px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-2xl mx-auto md:px-4">
          <button
            type="button"
            onClick={terminer}
            data-testid="visite-terminer-btn"
            className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold"
          >
            Terminer
          </button>
        </div>
      </footer>

      {apercu && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="denonce-modal">
          <div className="bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900">Aperçu de la dénonce</h3>
              <button
                type="button"
                onClick={() => setApercu(null)}
                data-testid="denonce-modal-close"
                className="w-11 h-11 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <p className="text-xs text-slate-500">À : {apercu.email_destinataire || '—'}</p>
              <p className="text-sm font-bold text-slate-900 mt-1">{apercu.sujet}</p>
              <pre className="mt-3 whitespace-pre-wrap text-sm text-slate-800 font-sans">{apercu.corps}</pre>
            </div>
            {!apercu.lecture && (
              <div className="p-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => envoyerDenonce(apercu.proprietaire_id)}
                  disabled={!apercu.email_destinataire || envoiEnCours === apercu.proprietaire_id}
                  data-testid="denonce-modal-envoyer"
                  className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold"
                >
                  {envoiEnCours === apercu.proprietaire_id ? 'Envoi…' : 'Envoyer'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
